import React from 'react';
import {
  Alert,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import InputMask from 'react-input-mask';
import Select from 'react-select';
import JumboTextField from '@jumbo/components/JumboFormik/JumboTextField';
import JumboSelectField from '@jumbo/components/JumboFormik/JumboSelectField';
import CircularProgress from '@mui/material/CircularProgress';
import { COUNTRIES } from 'app/utils/constants/countries';

// Etapa 1 do cadastro de um responsável novo (item 7.2): só os dados da
// pessoa, sem menor e sem turma. Ao concluir, o responsável é criado e o
// fluxo pergunta se deseja seguir para matricular um menor.
const FormularioCadastroResponsavelInicial = ({
  values,
  setFieldValue,
  handleChange,
  isLoading,
  showPassword,
  showPasswordConfirmation,
  handleClickShowPassword,
  handleClickShowPasswordConfirmation,
  handleMouseDownPassword,
  handleMouseDownPasswordConfirmation,
  checkCep,
}) => (
  <>
    <Alert severity='info' sx={{ mb: 3 }}>
      Preencha seus dados para se cadastrar como responsável.
    </Alert>

    {/* ========== DADOS PESSOAIS ========== */}
    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 2 }}>
      Dados Pessoais
    </Typography>

    <Grid container spacing={2}>
      <Grid item lg={5} xs={12}>
        <JumboTextField fullWidth name='nome' label='Nome Completo *' />
      </Grid>

      <Grid item lg={3} xs={12}>
        <JumboTextField
          fullWidth
          name='data_nascimento'
          type='date'
          label='Data de Nascimento *'
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item lg={4} xs={12}>
        <FormControl fullWidth>
          <InputLabel id='genero' shrink>Gênero</InputLabel>
          <JumboSelectField
            fullWidth
            id='genero'
            name='genero'
            label='Gênero'
            value={values.genero || ''}
            onChange={handleChange}
            notched
          >
            <MenuItem value=''>Selecione</MenuItem>
            <MenuItem value='masculino'>Masculino</MenuItem>
            <MenuItem value='feminino'>Feminino</MenuItem>
            <MenuItem value='outro'>Outro</MenuItem>
            <MenuItem value='prefiro_nao_informar'>Prefiro não informar</MenuItem>
          </JumboSelectField>
        </FormControl>
      </Grid>

      <Grid item lg={6} xs={12}>
        <InputMask mask='999.999.999-99' value={values.cpf} onChange={handleChange}>
          {(inputProps) => (
            <JumboTextField {...inputProps} fullWidth name='cpf' label='CPF *' type='text' />
          )}
        </InputMask>
      </Grid>

      <Grid item lg={6} xs={12}>
        <JumboTextField fullWidth name='rg' label='RG' />
      </Grid>
    </Grid>

    {/* ========== CONTATO ========== */}
    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 2, mt: 3 }}>
      Contato
    </Typography>

    <Grid container spacing={2}>
      <Grid item lg={6} xs={12}>
        <InputMask mask='+999(99)99999-9999' value={values.celular} onChange={handleChange}>
          {(inputProps) => (
            <JumboTextField
              {...inputProps}
              fullWidth
              name='celular'
              label='Celular (DDI, DDD e número) *'
              InputLabelProps={{ shrink: true }}
            />
          )}
        </InputMask>
      </Grid>

      <Grid item lg={6} xs={12}>
        <InputMask
          mask='+999(99)99999-9999'
          value={values.celular_confirmacao}
          onChange={handleChange}
          onPaste={(e) => e.preventDefault()}
        >
          {(inputProps) => (
            <JumboTextField
              {...inputProps}
              fullWidth
              name='celular_confirmacao'
              label='Confirmar Celular *'
              InputLabelProps={{ shrink: true }}
            />
          )}
        </InputMask>
      </Grid>

      <Grid item lg={6} xs={12}>
        <JumboTextField
          fullWidth
          name='telefone'
          label='Telefone Fixo (opcional)'
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item lg={6} xs={12} />

      <Grid item lg={6} xs={12}>
        <JumboTextField fullWidth name='email' label='Email *' type='email' />
      </Grid>

      <Grid item lg={6} xs={12}>
        <JumboTextField
          fullWidth
          name='email_confirmacao'
          label='Confirmar Email *'
          type='email'
          onPaste={(e) => e.preventDefault()}
        />
      </Grid>
    </Grid>

    {/* ========== ENDEREÇO ========== */}
    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 2, mt: 3 }}>
      Endereço
    </Typography>

    <Grid container spacing={2}>
      <Grid item lg={4} xs={12}>
        <FormControl fullWidth>
          <Select
            options={COUNTRIES}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onChange={(pais) => setFieldValue('pais', pais.value)}
            value={COUNTRIES.filter((option) => option.value === (values.pais || 'BR'))}
            placeholder='País'
          />
        </FormControl>
      </Grid>

      <Grid item lg={8} xs={12} />

      {values.pais === 'BR' ? (
        <>
          <Grid item lg={3} xs={12}>
            <InputMask
              mask='99999-999'
              value={values.CEP}
              onChange={handleChange}
              onBlur={(e) => checkCep(e, setFieldValue)}
            >
              {(inputProps) => (
                <JumboTextField {...inputProps} fullWidth name='CEP' label='CEP' />
              )}
            </InputMask>
          </Grid>

          <Grid item lg={1} xs={1}>
            {isLoading && <CircularProgress style={{ alignSelf: 'center' }} />}
          </Grid>

          <Grid item lg={6} xs={12}>
            <JumboTextField
              fullWidth
              name='endereco'
              label='Endereço *'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <JumboTextField
              fullWidth
              name='numero'
              label='Número *'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={4} xs={12}>
            <JumboTextField
              fullWidth
              name='complemento'
              label='Complemento'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={4} xs={12}>
            <JumboTextField
              fullWidth
              name='bairro'
              label='Bairro *'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={2} xs={6}>
            <FormControl fullWidth>
              <InputLabel id='uf'>UF *</InputLabel>
              <JumboSelectField labelId='uf' name='uf' label='UF'>
                <MenuItem value='AC'>Acre</MenuItem>
                <MenuItem value='AL'>Alagoas</MenuItem>
                <MenuItem value='AP'>Amapá</MenuItem>
                <MenuItem value='AM'>Amazonas</MenuItem>
                <MenuItem value='BA'>Bahia</MenuItem>
                <MenuItem value='CE'>Ceará</MenuItem>
                <MenuItem value='DF'>Distrito Federal</MenuItem>
                <MenuItem value='ES'>Espírito Santo</MenuItem>
                <MenuItem value='GO'>Goiás</MenuItem>
                <MenuItem value='MA'>Maranhão</MenuItem>
                <MenuItem value='MT'>Mato Grosso</MenuItem>
                <MenuItem value='MS'>Mato Grosso do Sul</MenuItem>
                <MenuItem value='MG'>Minas Gerais</MenuItem>
                <MenuItem value='PA'>Pará</MenuItem>
                <MenuItem value='PB'>Paraíba</MenuItem>
                <MenuItem value='PR'>Paraná</MenuItem>
                <MenuItem value='PE'>Pernambuco</MenuItem>
                <MenuItem value='PI'>Piauí</MenuItem>
                <MenuItem value='RJ'>Rio de Janeiro</MenuItem>
                <MenuItem value='RN'>Rio Grande do Norte</MenuItem>
                <MenuItem value='RS'>Rio Grande do Sul</MenuItem>
                <MenuItem value='RO'>Rondônia</MenuItem>
                <MenuItem value='RR'>Roraima</MenuItem>
                <MenuItem value='SC'>Santa Catarina</MenuItem>
                <MenuItem value='SP'>São Paulo</MenuItem>
                <MenuItem value='SE'>Sergipe</MenuItem>
                <MenuItem value='TO'>Tocantins</MenuItem>
              </JumboSelectField>
            </FormControl>
          </Grid>

          <Grid item lg={2} xs={6}>
            <JumboTextField
              fullWidth
              name='cidade'
              label='Cidade *'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <JumboTextField fullWidth name='endereco' label='Endereço Completo *' />
          </Grid>

          <Grid item lg={4} xs={12}>
            <JumboTextField fullWidth name='codigo_postal' label='Código Postal *' />
          </Grid>

          <Grid item lg={4} xs={12}>
            <JumboTextField fullWidth name='estado' label='Estado/Província *' />
          </Grid>

          <Grid item lg={4} xs={12}>
            <JumboTextField fullWidth name='cidade' label='Cidade *' />
          </Grid>
        </>
      )}
    </Grid>

    {/* ========== SENHA ========== */}
    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 2, mt: 3 }}>
      Senha de Acesso
    </Typography>

    <Grid container spacing={2}>
      <Grid item lg={6} xs={12}>
        <JumboTextField
          fullWidth
          id='senha'
          type={showPassword ? 'text' : 'password'}
          name='senha'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge='end'
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          label='Senha *'
        />
      </Grid>

      <Grid item lg={6} xs={12}>
        <JumboTextField
          fullWidth
          id='senhaConfirmacao'
          type={showPasswordConfirmation ? 'text' : 'password'}
          name='senhaConfirmacao'
          onPaste={(e) => e.preventDefault()}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={handleClickShowPasswordConfirmation}
                  onMouseDown={handleMouseDownPasswordConfirmation}
                  edge='end'
                >
                  {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          label='Confirmar Senha *'
        />
      </Grid>
    </Grid>
  </>
);

export default FormularioCadastroResponsavelInicial;
